import React from "react";
import { useActivityLogs } from "@/components/pages/dashboard/hooks/useActivityLogs";
import { ActivityLogItem } from "@/components/pages/dashboard/components/ActivityLogItem";
import { useAccessCapabilities } from "@/hooks/useAccessCapabilities";

interface ActivityLogContainerProps {
  // When the subscription lacks core features (checkin/checkout, etc.),
  // restricting to those services' activity would always return zero
  // results — show all logged activity for the hotel instead.
  showCoreFeatures?: boolean;
}

export const ActivityLogContainer: React.FC<ActivityLogContainerProps> = ({
  showCoreFeatures = true,
}) => {
  const { capabilities } = useAccessCapabilities();

  const now = new Date();
  const start = new Date(now);
  start.setHours(0, 0, 0, 0);

  const end = new Date(now);
  end.setHours(23, 59, 59, 999);

  const filters = {
    startDate: start.toISOString(),
    endDate: end.toISOString(),
    sort: "desc" as const,
    ...(showCoreFeatures
      ? {
          service: [
            ...(capabilities.canUseRequestsSystem
              ? ["RequestService", "RequestFeedbackService"]
              : []),
            "BookingCheckinService",
            "BookingCheckoutService",
          ],
        }
      : {}),
  };

  const { logs, isLoading } = useActivityLogs(filters);

  if (isLoading && logs.length === 0) {
    return (
      <div className="flex min-h-0 flex-1 items-center justify-center">
        <p className="text-xs text-neutral-400">Loading...</p>
      </div>
    );
  }

  if (logs.length === 0) {
    return (
      <div className="flex min-h-0 flex-1 items-center justify-center">
        <p className="text-xs text-neutral-400">No activity yet</p>
      </div>
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto pr-1">
      {logs.map((log) => (
        <ActivityLogItem key={log.id} log={log} />
      ))}
    </div>
  );
};
